import { defineMeta } from '@/registry/types';

export const cardMeta = defineMeta({
  name: 'Card',
  level: 'organism',
  category: 'surface',
  classification: 'mui-overrides',
  description:
    'Bordered surface grouping related content. Default is themed MUI Card; variant="info" is a static summary card (32px vertical / 24px horizontal padding) and variant="data" is a collapsible card for text, tables or lists. Use CardDataTag for inline entity pills.',
  baseLibrary: '@mui/material',
  muiEquivalent: 'Card',
  muiDocs: 'https://mui.com/material-ui/react-card/',
  a2uiEquivalent: 'Card',
  importPath: '@/organisms/Card',
  optionalProps: ['elevation', 'variant', 'title', 'emptyText', 'defaultExpanded', 'expanded', 'onChange'],
  customVariants: ['info', 'data'],
  supportedStates: ['default', 'hover', 'selected', 'expanded', 'collapsed', 'empty'],
  designTokens: ['colors', 'radius', 'shadows', 'spacing', 'typography'],
  accessibility: [
    'Use semantic headings inside CardHeader; make whole-card click targets accessible.',
    'The data variant header is a button with aria-expanded.',
  ],
  usageExamples: [
    '<Card><CardHeader title="prod-db-01" subheader="EC2 instance" /><CardContent><Typography variant="body1">...</Typography></CardContent></Card>',
    '<Card variant="info" title="Overview"><Typography variant="body1">...<CardDataTag>10.0.0.42</CardDataTag></Typography></Card>',
    '<Card variant="data" title="Historical Cases"><DataTable columns={columns} data={rows} bordered={false} surface="transparent" /></Card>',
  ],
  rules: [
    'CardHeader title is subtitle1 and the subheader is body1 (theme defaults). Body copy is `Typography variant="body1"`.',
    'Info/data titles are subtitle1; the body slot applies body1 via `sx.typography` so tables are not wrapped in a `<p>`.',
    'A card is separated from its host by the tonal step of the surface ladder, not by a border. It takes the next rung up from whatever contains it, so it does not need to be told which background to use.',
    'A card opens a surface scope at that next rung, so nested tables inherit widget header fills and row separators rather than the page\'s. Put a table inside a data card with `bordered={false} surface="transparent"`.',
  ],
  antiPatterns: [
    'Do not put a card inside a card inside a card. Three levels of containment is the ceiling; a fourth paints identically to the third and logs an error, so the nesting buys nothing. If you need a fourth, flatten the structure or move the deepest content to its own route.',
    'Do not add a border to a card. The tonal step already separates it, and a border on top of it reads as a second, competing edge.',
    'Do not use variant="info" for collapsible sections (use variant="data").',
  ],
  mountsSurface: 'inline',
  llmSafe: true,
  whenToUse: ['Summaries, entity tiles, dashboards', 'Investigation overviews (info)', 'Optional detail sections (data)'],
  whenNotToUse: ['Full-page layout (use templates)'],
});

export default cardMeta;
